import type { BusinessHours, ScheduleDay, WeekHours } from '../api/schemas';

/** Sunday=0..Saturday=6, matching Date.getDay() and the schedule PATCH contract. */
export const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;

export const WEEKDAY_LABEL: Record<(typeof WEEKDAYS)[number], string> = {
  0: 'Domingo',
  1: 'Lunes',
  2: 'Martes',
  3: 'Miércoles',
  4: 'Jueves',
  5: 'Viernes',
  6: 'Sábado',
};

const WEEKDAY_KEYS: Record<(typeof WEEKDAYS)[number], keyof WeekHours> = {
  0: 'sun',
  1: 'mon',
  2: 'tue',
  3: 'wed',
  4: 'thu',
  5: 'fri',
  6: 'sat',
};

/** Whole-hour clock string the schedule PATCH endpoint accepts, e.g. "09:00". */
export function toHourClock(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

/** Inverse of `toHourClock`; returns undefined for anything not on the hour. */
export function fromHourClock(value: string): number | undefined {
  const match = /^([0-2]\d):00$/.exec(value);
  if (!match) return undefined;
  return Number(match[1]);
}

/**
 * Converts the business's read-model hours (a shared window, a per-day map,
 * or nothing at all) into one editable row per weekday. A day with no
 * resolvable window is closed: that mirrors the read model's own rule that an
 * absent day with no default is closed.
 */
export function hoursToScheduleDays(hours: BusinessHours | undefined): ScheduleDay[] {
  return WEEKDAYS.map((dayOfWeek) => {
    const window = resolveDayWindow(hours, dayOfWeek);
    if (!window) return { dayOfWeek, isClosed: true };
    const [opensHour, closesHour] = window;
    return { dayOfWeek, opensAt: toHourClock(opensHour), closesAt: toHourClock(closesHour) };
  });
}

function resolveDayWindow(
  hours: BusinessHours | undefined,
  dayOfWeek: (typeof WEEKDAYS)[number],
): [number, number] | undefined {
  if (!hours) return undefined;
  if (Array.isArray(hours)) return hours;

  const key = WEEKDAY_KEYS[dayOfWeek];
  const explicit = hours[key];
  if (explicit === null) return undefined;
  if (explicit) return explicit;
  return hours.default;
}
